import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiOrNull, MARKETPLACE, marketplaceKeys } from './client';
import type {
  AvailabilityRange,
  AvailabilityRangeParams,
  CreateHoldRequest,
  Hold,
} from './contracts';
import { errorCode, isApiError, isNotImplemented } from './errors';

// ─── §3 Multi-day availability ─────────────────────────────────────────

/** Query string for `/availability/range` (stable order → stable cache key). */
export function buildAvailabilityRangeQuery(params: AvailabilityRangeParams): string {
  const qs = new URLSearchParams();
  if (params.serviceId) qs.set('serviceId', params.serviceId);
  if (params.from) qs.set('from', params.from);
  qs.set('days', String(params.days ?? 7));
  if (params.employeeId) qs.set('employeeId', params.employeeId);
  if (params.locationId) qs.set('locationId', params.locationId);
  if (params.bookingMode) qs.set('bookingMode', params.bookingMode);
  if (params.excludeAppointmentId) qs.set('excludeAppointmentId', params.excludeAppointmentId);
  return qs.toString();
}

/**
 * Multi-day slots for the time step (§3). Resolves `null` while the backend
 * doesn't have the endpoint yet — callers fall back to `useAvailability`.
 * Same enable rule as `useAvailability`: AT_LOCATION needs a locationId,
 * MOBILE/ONLINE don't.
 */
export function useAvailabilityRange(subdomain: string | undefined, params: AvailabilityRangeParams) {
  const locationless = params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE';
  const enabled = !!(
    subdomain &&
    params.serviceId &&
    params.from &&
    (params.locationId || locationless)
  );
  const qs = buildAvailabilityRangeQuery(params);
  return useQuery<AvailabilityRange | null>({
    queryKey: marketplaceKeys.availabilityRange(subdomain ?? '', qs),
    enabled,
    queryFn: () =>
      apiOrNull<AvailabilityRange>(`${MARKETPLACE}/salon/${subdomain}/availability/range?${qs}`),
    staleTime: 30_000,
  });
}

// ─── §4 Slot holds ─────────────────────────────────────────────────────

/**
 * Holds a slot for 10 minutes as soon as it's picked (§4). Resolves the
 * `Hold`, or `null` when the backend has no hold endpoint yet (the booking
 * then proceeds without a `holdId`). A `409 slot_taken` rejects with an
 * `ApiError` and refreshes every slot list so the taken time disappears.
 */
export function useCreateHold(subdomain: string) {
  const qc = useQueryClient();
  return useMutation<Hold | null, Error, CreateHoldRequest>({
    mutationFn: (req) =>
      apiOrNull<Hold>(`${MARKETPLACE}/salon/${subdomain}/holds`, {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onError: (err) => {
      if (errorCode(err) === 'slot_taken' || (isApiError(err) && err.status === 409)) {
        qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityAll });
        qc.invalidateQueries({ queryKey: marketplaceKeys.availabilityRangeAll });
      }
    },
  });
}

/**
 * Releases a hold (user picked another slot or left the flow). Best-effort:
 * an already-expired/unknown hold (404/410) or a backend without the endpoint
 * resolves quietly.
 */
export function useReleaseHold() {
  return useMutation<void, Error, string>({
    mutationFn: async (holdId) => {
      try {
        await api<void>(`${MARKETPLACE}/holds/${encodeURIComponent(holdId)}`, {
          method: 'DELETE',
        });
      } catch (e) {
        if (isNotImplemented(e) || (isApiError(e) && (e.status === 404 || e.status === 410))) {
          return;
        }
        throw e;
      }
    },
  });
}
